import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IoChatbubbles, IoCloudOfflineOutline, IoRefresh } from 'react-icons/io5';
import { track } from '../../utils/track';
import './SupportChat.css';

// Mesma tela de suporte do sette-app. O HTML é gerado por scripts/sync-support-chat.js.
const CHAT_PAGE = '/suporte-chat.html';
const CHAT_MESSAGE_SOURCE = 'sette-support-chat';
const OPEN_CHAT_EVENT = 'sette-chat:open';

export const HIRE_MESSAGE =
  'Olá! Entrei em contato através do site e tenho interesse em contratar os serviços. (não apague esta mensagem)';

// Abre o chat de qualquer lugar do site; com `message`, ela é enviada automaticamente.
export function openSupportChat(message) {
  window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT, { detail: { message } }));
}

// (xx) XXXXX-XXXX; fixo (10 dígitos) fica (xx) XXXX-XXXX.
function formatPhone(value) {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : '';
  const split = d.length > 10 ? 7 : 6;
  return `(${d.slice(0, 2)}) ${d.slice(2, split)}${d.length > split ? `-${d.slice(split)}` : ''}`;
}

export default function SupportChat() {
  const { pathname } = useLocation();
  const isBackOffice = pathname.startsWith('/admin') || pathname === '/login';

  const iframeRef = useRef(null);
  const pendingResendRef = useRef(null);
  const isOpenRef = useRef(false);
  const isReadyRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  // O chat só carrega no primeiro clique e continua conectado quando minimizado.
  const [isMounted, setIsMounted] = useState(false);
  const [session, setSession] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [unread, setUnread] = useState(0);
  // Antes do chat real, o visitante informa nome e contato (guardados só nesta aba).
  const [lead, setLead] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('sette-chat-lead'));
    } catch {
      return null;
    }
  });
  const [leadName, setLeadName] = useState('');
  const [leadInput, setLeadInput] = useState('');

  isOpenRef.current = isOpen;
  isReadyRef.current = isReady && !hasError;

  const runInChat = useCallback((callback) => {
    try {
      const chatWindow = iframeRef.current?.contentWindow;
      if (chatWindow) callback(chatWindow);
    } catch (error) {
      console.warn('[Sette Chat]', error);
    }
  }, []);

  const openChat = useCallback(() => {
    setIsMounted(true);
    setIsOpen(true);
    setUnread(0);
    runInChat((chatWindow) => chatWindow.refreshOpaChat?.());
  }, [runInChat]);

  useEffect(() => {
    const handleOpen = (event) => {
      const message = event.detail?.message;
      track('chat_open', { chat_intent: message === HIRE_MESSAGE ? 'contratar' : 'suporte', page_path: window.location.pathname });
      openChat();
      if (!message) return;
      // Com o chat pronto envia na hora; senão, assim que ele avisar "opa-ready".
      if (isReadyRef.current) runInChat((chatWindow) => chatWindow.sendChatText?.(message));
      else pendingResendRef.current = message;
    };
    window.addEventListener(OPEN_CHAT_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_CHAT_EVENT, handleOpen);
  }, [openChat, runInChat]);

  const closeChat = useCallback(() => setIsOpen(false), []);

  // Recomeça o chat com uma conexão nova, como sair e voltar para a tela no app.
  const restartChat = useCallback((textToResend) => {
    pendingResendRef.current = textToResend;
    setHasError(false);
    setIsReady(false);
    setSession((value) => value + 1);
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin || event.data?.source !== CHAT_MESSAGE_SOURCE) return;
      const message = String(event.data.message || '');

      if (message.startsWith('opa-incoming:')) {
        if (!isOpenRef.current || document.hidden) setUnread((value) => value + 1);
        return;
      }
      if (message.startsWith('open-url:')) {
        const url = decodeURIComponent(message.slice('open-url:'.length));
        if (/^https?:\/\//i.test(url)) window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (message === 'opa-back') {
        closeChat();
        return;
      }
      if (message === 'opa-closed') {
        // O "X" encerra o atendimento: fecha o painel e a próxima abertura começa do zero.
        closeChat();
        restartChat(null);
        return;
      }
      if (message === 'opa-restart' || message.startsWith('opa-restart:')) {
        const text = message.startsWith('opa-restart:') ? decodeURIComponent(message.slice('opa-restart:'.length)) : null;
        restartChat(text || null);
        return;
      }
      if (message === 'opa-error') {
        setHasError(true);
        setIsReady(true);
        return;
      }
      if (message.startsWith('opa-route:') || message.startsWith('opa-user:') ||message.startsWith('opa-enhancement-error:')) {
        return;
      }

      setHasError(false);
      setIsReady(true);
      if (message === 'opa-ready' && pendingResendRef.current) {
        const text = pendingResendRef.current;
        pendingResendRef.current = null;
        runInChat((chatWindow) => chatWindow.sendChatText?.(text));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [closeChat, restartChat, runInChat]);

  // Ao voltar para a aba, busca as respostas mais recentes.
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) runInChat((chatWindow) => chatWindow.refreshOpaChat?.());
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [runInChat]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') closeChat();
    };
    document.addEventListener('keydown', handleKey);
    document.body.classList.add('sette-chat-open');
    // No celular o chat ocupa a tela toda: trava a página atrás. Só overflow:hidden
    // não segura o scroll no iOS, por isso o body fica fixo na posição atual.
    const lockScroll = window.matchMedia('(max-width: 600px)').matches;
    const scrollY = window.scrollY;
    if (lockScroll) {
      Object.assign(document.body.style, { position: 'fixed', top: `-${scrollY}px`, left: '0', right: '0' });
    }
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.classList.remove('sette-chat-open');
      if (lockScroll) {
        Object.assign(document.body.style, { position: '', top: '', left: '', right: '' });
        window.scrollTo(0, scrollY);
      }
    };
  }, [isOpen, closeChat]);

  useEffect(() => {
    if (isBackOffice) setIsOpen(false);
  }, [isBackOffice]);

  if (isBackOffice) return null;

  const isAskingPhone = !lead && leadName !== '';
  const submitLead = (event) => {
    event.preventDefault();
    const value = isAskingPhone ? formatPhone(leadInput) : leadInput.trim();
    if (!isAskingPhone) {
      if (value) {
        track('chat_lead_name');
        setLeadName(value);
        setLeadInput('');
      }
      return;
    }
    if (value.replace(/\D/g, '').length < 10) return;
    const data = { name: leadName, phone: value };
    try {
      sessionStorage.setItem('sette-chat-lead', JSON.stringify(data));
    } catch {
      // sem storage: pergunta de novo na próxima abertura
    }
    pendingResendRef.current = pendingResendRef.current || HIRE_MESSAGE;
    track('generate_lead', { lead_source: 'chat_site', page_path: window.location.pathname });
    setLead(data);
  };

  return (
    <>
      <button
        type="button"
        className={`sette-chat-launcher${isOpen ? ' is-hidden' : ''}`}
        onClick={() => {
          track('chat_open', { chat_intent: 'suporte', page_path: window.location.pathname });
          openChat();
        }}
        aria-label={unread ? `Abrir chat de atendimento, ${unread} novas mensagens` : 'Abrir chat de atendimento'}
        aria-expanded={isOpen}
        aria-controls="sette-chat-panel"
      >
        <span className="sette-chat-launcher__label">Fale conosco</span>
        <span className="sette-chat-launcher__icon">
          <img src="/assets/chat/fale-conosco.png" alt="" />
        </span>
        {unread > 0 && <span className="sette-chat-launcher__badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {isMounted && (
        <div
          id="sette-chat-panel"
          className={`sette-chat-panel${isOpen ? ' is-open' : ''}`}
          role="dialog"
          aria-label="Chat de atendimento SETTE Fibra"
          aria-hidden={!isOpen}
        >
          {lead && (
            <iframe
              key={session}
              ref={iframeRef}
              src={CHAT_PAGE}
              title="Atendimento SETTE Fibra"
              className="sette-chat-panel__frame"
              allow="microphone; autoplay; clipboard-write"
            />
          )}

          {!lead && (
            <form className="sette-chat-state" onSubmit={submitLead}>
              <span className="sette-chat-state__icon is-loading">
                <IoChatbubbles aria-hidden="true" />
              </span>
              <strong className="sette-chat-state__title">
                {isAskingPhone ? `Prazer, ${leadName}!` : 'Olá! Vamos começar?'}
              </strong>
              <label className="sette-chat-state__text" htmlFor="sette-chat-lead-input">
                {isAskingPhone ? 'Qual o seu número de contato (com DDD)?' : 'Qual é o seu nome?'}
              </label>
              <input
                key={isAskingPhone ? 'phone' : 'name'}
                id="sette-chat-lead-input"
                className="sette-chat-state__input"
                type={isAskingPhone ? 'tel' : 'text'}
                inputMode={isAskingPhone ? 'tel' : 'text'}
                autoComplete={isAskingPhone ? 'tel' : 'name'}
                placeholder={isAskingPhone ? '(62) 99999-9999' : 'Seu nome'}
                value={leadInput}
                onChange={(event) => setLeadInput(isAskingPhone ? formatPhone(event.target.value) : event.target.value)}
                autoFocus
              />
              <button type="submit" className="sette-chat-state__retry">Continuar</button>
              <button type="button" className="sette-chat-state__close" onClick={closeChat} aria-label="Minimizar conversa">×</button>
            </form>
          )}

          {lead && !isReady && (
            <div className="sette-chat-state">
              <span className="sette-chat-state__icon is-loading">
                <IoChatbubbles aria-hidden="true" />
              </span>
              <strong className="sette-chat-state__title">Conectando você à SETTE</strong>
              <span className="sette-chat-state__text">Preparando um atendimento rápido e seguro...</span>
              <span className="sette-chat-state__spinner" aria-label="Carregando" />
              <button type="button" className="sette-chat-state__close" onClick={closeChat} aria-label="Minimizar conversa">×</button>
            </div>
          )}

          {hasError && (
            <div className="sette-chat-state is-error">
              <span className="sette-chat-state__icon">
                <IoCloudOfflineOutline aria-hidden="true" />
              </span>
              <strong className="sette-chat-state__title">Não foi possível abrir o chat</strong>
              <span className="sette-chat-state__text">
                Verifique sua conexão e tente novamente. Seu atendimento continua a um toque de distância.
              </span>
              <button type="button" className="sette-chat-state__retry" onClick={() => restartChat(null)}>
                <IoRefresh aria-hidden="true" /> Tentar novamente
              </button>
              <button type="button" className="sette-chat-state__close" onClick={closeChat} aria-label="Minimizar conversa">×</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
